/**
 * useOceanFrames.ts
 * يقرأ مانيفست طبقة المحيط (شذوذ حرارة سطح البحر — النينيو) من `rasters/ocean/meta.json`.
 *
 * **لماذا مانيفست منفصل عن `rasters/meta.json`** — هذا جوهر الطبقة لا تفصيلة تنظيمية:
 * كل محور الزمن في التطبيق مبنيّ على «ساعة توقّع رقم N من دورة run_epoch»، بينما هذه
 * الطبقة **أيّام رصد ماضية** من NOAA لا علاقة لها بأي دورة نموذج. تمريرها في المسار
 * العادي كان يعني قراءة يومٍ في خانة ساعة — أي وضع الإطارات في زمن خاطئ بالكامل.
 *
 * يُستهلَك في مكان واحد (NewWeatherMap) الذي يبني منه محور الشريط الزمني وروابط النسيج
 * معاً، فيستحيل أن يفترق المحور عن الصورة المعروضة عليه.
 */

import { useEffect, useMemo, useState } from 'react';
import { rasterUrl } from '../../../services/rasterVersion';

export interface OceanFrame {
    index: number;
    /** اسم ملفّ PNG كما كتبه المولّد (يُبدَّل امتداده إلى webp عند إعلان المانيفست ذلك). */
    file: string;
    /** التاريخ الميلادي لليوم الذي يمثّله الإطار (YYYY-MM-DD). */
    date: string;
    epoch: number;
    quality?: 'final' | 'preliminary';
}

export interface OceanMeta {
    layer: string;
    source: string;
    climatology: string;
    range: [number, number];
    ext: 'png' | 'webp';
    frames: OceanFrame[];
    generated_epoch: number;
}

export const OCEAN_RASTER_DIR = 'rasters/ocean/';

/**
 * دورة التحديث. الكرون يومي، لكن ساعة التوليد ليست مضمونة (تأخّر NOAA يتراوح)، فنستطلع
 * كل ساعة: الطلب واحد صغير، والبديل هو أن يبقى زائرٌ فتح الصفحة صباحاً على بيانات أمس
 * طوال اليوم.
 */
const REFRESH_MS = 60 * 60 * 1000;

export function useOceanFrames(enabled: boolean): OceanMeta | null {
    const [meta, setMeta] = useState<OceanMeta | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        const load = () => {
            fetch(`${import.meta.env.BASE_URL}${OCEAN_RASTER_DIR}meta.json?v=${Math.floor(Date.now() / REFRESH_MS)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((m: OceanMeta | null) => {
                    if (cancelled || !m) return;
                    // نقبل فقط الإطارات المكتملة الحقول. مانيفست نصف مكتوب (توليد متعثّر)
                    // كان سيبني روابط `sst-anomaly_undefined.png` فيُمطر الخادم بـ404.
                    const frames = (m.frames ?? []).filter(
                        (f) => !!f?.file && Number.isFinite(f?.epoch),
                    );
                    if (frames.length) setMeta({ ...m, frames });
                })
                .catch(() => { /* الطبقة غير مولَّدة بعد — تبقى فارغة والشريط على محوره العادي */ });
        };
        load();
        const id = window.setInterval(load, REFRESH_MS);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [enabled]);

    return meta;
}

/**
 * باني روابط إطارات المحيط — يحمل امتداد المانيفست وإصداره لا العالميّين.
 *
 * `generated_epoch` هو رقم الإصدار الصحيح هنا: يتغيّر مرّة واحدة يومياً عند التوليد
 * الفعلي، فيبقى كاش المتصفّح الطويل صالحاً بين التوليدات — بخلاف `run_epoch` الذي
 * يتغيّر كل ٦ ساعات لأسباب لا تخصّ هذه الطبقة.
 */
export function useOceanUrl(meta: OceanMeta | null) {
    return useMemo(() => {
        const ext = meta?.ext === 'webp' ? 'webp' : 'png';
        const version = meta?.generated_epoch ?? 0;
        return (file: string) => rasterUrl(OCEAN_RASTER_DIR, file, { ext, version });
    }, [meta?.ext, meta?.generated_epoch]);
}
